/**
 * Phase B.4 — derive relationship dynamics from existing data.
 *
 * No new LLM calls (Apple Foundation Models guardrails refuse too
 * often on real conversational content for B.4 to depend on fresh
 * extractions). Everything here is pure math on data we already have:
 *   - Interactions table: timing, duration, session count
 *   - Signals table: how much we've learned (depth proxy)
 *   - Per-session facts_json.sentiment: already extracted
 *
 * Outputs four narrow signals we can show honestly:
 *   1. Cadence — how often you talk, time since last contact
 *   2. Depth — minutes spent, signals learned per session
 *   3. Tone — sentiment distribution, dominant mood
 *   4. Trajectory — early vs recent comparison (tone shift + signal rate)
 *
 * Every label threshold is chosen to fail closed: when there's too
 * little data to make a claim, we return `null` rather than guess.
 * A relationship with 1 session doesn't have a "cadence" — don't invent
 * one.
 */
import type { ContactSignal, ProcessedInteraction } from "@/types/voice";

export type CadenceLabel =
  | "daily"
  | "weekly"
  | "monthly"
  | "sparse"
  | "rare";

export type TrajectoryLabel =
  | "warming"
  | "cooling"
  | "steady"
  | "new"
  | null;

export interface RelationshipDynamics {
  // Cadence
  totalSessions: number;
  daysSinceFirst: number | null;
  averageGapDays: number | null;
  daysSinceLast: number | null;
  cadenceLabel: CadenceLabel | null;

  // Depth
  totalMinutes: number;
  averageSessionMinutes: number | null;
  signalCount: number;
  signalsPerSession: number | null;

  // Tone
  sentimentCounts: Record<string, number>;
  dominantSentiment: string | null;

  // Trajectory
  trajectoryLabel: TrajectoryLabel;
  earlySentiment: string | null;
  recentSentiment: string | null;

  isEmpty: boolean;
}

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/**
 * `now` is parameterized for testability. Callers in production should
 * pass `new Date()`.
 */
export function buildRelationshipDynamics(
  interactions: ProcessedInteraction[],
  signals: ContactSignal[],
  now: Date
): RelationshipDynamics {
  const empty: RelationshipDynamics = {
    totalSessions: 0,
    daysSinceFirst: null,
    averageGapDays: null,
    daysSinceLast: null,
    cadenceLabel: null,
    totalMinutes: 0,
    averageSessionMinutes: null,
    signalCount: 0,
    signalsPerSession: null,
    sentimentCounts: {},
    dominantSentiment: null,
    trajectoryLabel: null,
    earlySentiment: null,
    recentSentiment: null,
    isEmpty: true,
  };

  if (interactions.length === 0) return empty;

  const sortedAsc = [...interactions].sort((a, b) =>
    a.createdAt.localeCompare(b.createdAt)
  );
  const first = sortedAsc[0];
  const last = sortedAsc[sortedAsc.length - 1];

  const totalSessions = sortedAsc.length;
  const daysSinceFirst = daysBetween(new Date(first.createdAt), now);
  const daysSinceLast = daysBetween(new Date(last.createdAt), now);

  const averageGapDays =
    totalSessions >= 2
      ? daysBetween(new Date(first.createdAt), new Date(last.createdAt)) /
        (totalSessions - 1)
      : null;

  const cadenceLabel = pickCadenceLabel(averageGapDays, totalSessions);

  const totalSeconds = sortedAsc.reduce(
    (acc, i) => acc + i.totalSecondsSpoken,
    0
  );
  const totalMinutes = totalSeconds / 60;
  const averageSessionMinutes =
    totalSessions > 0 ? totalMinutes / totalSessions : null;

  const signalCount = signals.length;
  const signalsPerSession =
    totalSessions > 0 ? signalCount / totalSessions : null;

  const sentimentCounts: Record<string, number> = {};
  for (const i of sortedAsc) {
    const s = i.facts?.sentiment?.trim();
    if (!s) continue;
    sentimentCounts[s] = (sentimentCounts[s] ?? 0) + 1;
  }
  const dominantSentiment = pickDominant(sentimentCounts);

  // Trajectory — compare earliest third against latest third when
  // there are enough sessions. Thirds are more stable than "first"
  // vs "last" for small N because a single unusual session can't
  // dominate.
  const trajectory = computeTrajectory(sortedAsc);

  return {
    totalSessions,
    daysSinceFirst,
    averageGapDays,
    daysSinceLast,
    cadenceLabel,
    totalMinutes,
    averageSessionMinutes,
    signalCount,
    signalsPerSession,
    sentimentCounts,
    dominantSentiment,
    trajectoryLabel: trajectory.label,
    earlySentiment: trajectory.earlySentiment,
    recentSentiment: trajectory.recentSentiment,
    isEmpty: false,
  };
}

function daysBetween(a: Date, b: Date): number {
  return Math.max(0, Math.abs(b.getTime() - a.getTime()) / MS_PER_DAY);
}

function pickCadenceLabel(
  avgGapDays: number | null,
  totalSessions: number
): CadenceLabel | null {
  // One-off conversations don't have a cadence yet — refuse to guess.
  if (avgGapDays === null || totalSessions < 2) return null;
  if (avgGapDays <= 1.5) return "daily";
  if (avgGapDays <= 9) return "weekly";
  if (avgGapDays <= 35) return "monthly";
  if (avgGapDays <= 120) return "sparse";
  return "rare";
}

function pickDominant(counts: Record<string, number>): string | null {
  const entries = Object.entries(counts);
  if (entries.length === 0) return null;
  entries.sort((a, b) => b[1] - a[1]);
  return entries[0][0];
}

interface TrajectoryResult {
  label: TrajectoryLabel;
  earlySentiment: string | null;
  recentSentiment: string | null;
}

const WARMTH_SCORE: Record<string, number> = {
  excited: 2,
  warm: 1,
  reflective: 0,
  neutral: 0,
  tense: -1,
};

function computeTrajectory(
  sortedAsc: ProcessedInteraction[]
): TrajectoryResult {
  if (sortedAsc.length < 3) {
    return {
      label: "new",
      earlySentiment: null,
      recentSentiment: null,
    };
  }

  const third = Math.max(1, Math.floor(sortedAsc.length / 3));
  const early = sortedAsc.slice(0, third);
  const recent = sortedAsc.slice(-third);

  const earlySentiment = modalSentiment(early);
  const recentSentiment = modalSentiment(recent);

  const earlyScore = averageWarmth(early);
  const recentScore = averageWarmth(recent);

  const delta = recentScore - earlyScore;
  let label: TrajectoryLabel;
  if (delta > 0.4) label = "warming";
  else if (delta < -0.4) label = "cooling";
  else label = "steady";

  return { label, earlySentiment, recentSentiment };
}

function modalSentiment(
  interactions: ProcessedInteraction[]
): string | null {
  const counts: Record<string, number> = {};
  for (const i of interactions) {
    const s = i.facts?.sentiment?.trim();
    if (!s) continue;
    counts[s] = (counts[s] ?? 0) + 1;
  }
  return pickDominant(counts);
}

function averageWarmth(interactions: ProcessedInteraction[]): number {
  const scores = interactions
    .map((i) => i.facts?.sentiment?.trim())
    .filter((s): s is string => Boolean(s))
    .map((s) => WARMTH_SCORE[s] ?? 0);
  if (scores.length === 0) return 0;
  return scores.reduce((acc, n) => acc + n, 0) / scores.length;
}
